// src/theme/nhsTheme.js

import { createTheme } from "@mui/material/styles";

const mybotgpTheme = createTheme({
  palette: {
    background: {
      default: "#FFFFFF",
      paper: "#FFFFFF",
    },
    text: {
      primary: "#333333",
    },
    primary: {
      main: "#005EB8", // NHS blue
      contrastText: "#ffffff",
    },
    secondary: {
      main: "#C9C9C9",
    },
  },
  typography: {
    fontFamily: [
      "ui-sans-serif",
      "-apple-system",
      "system-ui",
      "Segoe UI",
      "Helvetica",
      "Apple Color Emoji",
      "Arial",
      "sans-serif",
      "Segoe UI Emoji",
      "Segoe UI Symbol",
    ].join(","),
    h4: {
      fontWeight: 700,
    },
    h6: {
      fontWeight: 700,
      fontSize: "1.1rem",
    },
    subtitle1: {
      fontSize: "1rem",
      fontWeight: 600,
    },
  },

  components: {
    // TextField compact variant not working
    MuiTextField: {
      variants: [
        {
          props: { variant: "dense" },
          style: {
            "& .MuiInputBase-root": {
              fontSize: "0.75rem",
              height: "26px",
            },
            "& .MuiInputBase-input": {
              padding: "2px 6px",
            },
          },
        },
      ],
    },

    // Select compact variant not working
    MuiSelect: {
      variants: [
        {
          props: { variant: "dense" },
          style: {
            fontSize: "0.75rem",
            height: "26px",
            "& .MuiSelect-select": {
              padding: "2px 6px",
            },
          },
        },
      ],
    },

    // Button compact variant
    MuiButton: {
      variants: [
        {
          props: { variant: "dense" },
          style: {
            fontSize: "0.7rem",
            padding: "2px 6px",
            minHeight: "26px",
            lineHeight: "1.2",
            border: "1px solid rgba(0,0,0,0.3)",
            borderRadius: "6px",
            transition: "all 0.2s ease-in-out",
            "&:hover": {
              borderColor: "#1976d2",
              backgroundColor: "rgba(25, 118, 210, 0.1)",
            },
            "&:active": {
              borderColor: "#115293",
              backgroundColor: "rgba(25, 118, 210, 0.2)",
            },
          },
        },
      ],
    },

    MuiDataGrid: {
      styleOverrides: {
        root: {
          border: "none",
          backgroundColor: "#E6F7FC",

          "& .MuiDataGrid-row.even": { backgroundColor: "#f9f9f9" },
          "& .MuiDataGrid-row.odd": { backgroundColor: "#ffffff" },
          "& .MuiDataGrid-row:hover": { backgroundColor: "#e3f2fd" },

          // Green cell backgrounds
          "& .MuiDataGrid-row.even .MuiDataGrid-cell.bg-green": {
            backgroundColor: "#d9f0df",
          },
          "& .MuiDataGrid-row.odd .MuiDataGrid-cell.bg-green": {
            backgroundColor: "#e6f4ea",
          },
          "& .MuiDataGrid-row:hover .MuiDataGrid-cell.bg-green": {
            backgroundColor: "#c8e6c9",
          },

          // Orange cell backgrounds
          "& .MuiDataGrid-row.even .MuiDataGrid-cell.bg-orange": {
            backgroundColor: "#ffeacc",
          },
          "& .MuiDataGrid-row.odd .MuiDataGrid-cell.bg-orange": {
            backgroundColor: "#fff4e5",
          },
          "& .MuiDataGrid-row:hover .MuiDataGrid-cell.bg-orange": {
            backgroundColor: "#ffe0b2",
          },

          // Pink cell backgrounds
          "& .MuiDataGrid-row.even .MuiDataGrid-cell.bg-pink": {
            backgroundColor: "#f8bbd0",
          },
          "& .MuiDataGrid-row.odd .MuiDataGrid-cell.bg-pink": {
            backgroundColor: "#fce4ec",
          },
          "& .MuiDataGrid-row:hover .MuiDataGrid-cell.bg-pink": {
            backgroundColor: "#f48fb1",
          },

          // Blue cell backgrounds
          "& .MuiDataGrid-row.even .MuiDataGrid-cell.bg-blue": {
            backgroundColor: "#d0e8ff",
          },
          "& .MuiDataGrid-row.odd .MuiDataGrid-cell.bg-blue": {
            backgroundColor: "#e3f2fd",
          },
          "& .MuiDataGrid-row:hover .MuiDataGrid-cell.bg-blue": {
            backgroundColor: "#bbdefb",
          },

          // Yellow cell backgrounds
          "& .MuiDataGrid-row.even .MuiDataGrid-cell.bg-yellow": {
            backgroundColor: "#fff9c4",
          },
          "& .MuiDataGrid-row.odd .MuiDataGrid-cell.bg-yellow": {
            backgroundColor: "#fffde7",
          },
          "& .MuiDataGrid-row:hover .MuiDataGrid-cell.bg-yellow": {
            backgroundColor: "#fff59d",
          },

          // Light cell backgrounds
          "& .MuiDataGrid-row.even .MuiDataGrid-cell.bg-light": {
            backgroundColor: "#f5f5f5",
          },
          "& .MuiDataGrid-row.odd .MuiDataGrid-cell.bg-light": {
            backgroundColor: "#fafafa",
          },
          "& .MuiDataGrid-row:hover .MuiDataGrid-cell.bg-light": {
            backgroundColor: "#eeeeee",
          },

          // Grey cell backgrounds
          "& .MuiDataGrid-row.even .MuiDataGrid-cell.bg-grey": {
            backgroundColor: "#e0e0e0",
          },
          "& .MuiDataGrid-row.odd .MuiDataGrid-cell.bg-grey": {
            backgroundColor: "#eeeeee",
          },
          "& .MuiDataGrid-row:hover .MuiDataGrid-cell.bg-grey": {
            backgroundColor: "#bdbdbd",
          },
        },

        columnHeaders: {
          "--DataGrid-t-header-background-base": "#4DA5E2",
          backgroundColor: "#4DA5E2",
          color: "#FFFFFF",
          fontWeight: 700,
          height: 25,
          minHeight: 25,
          maxHeight: 25,
        },
      },

      // Compact "dense" variant

      variants: [
        {
          props: { variant: "superdense" },
          style: {
            "& .MuiDataGrid-columnHeaders": {
              fontSize: "0.8rem",
              minHeight: "28px !important",
              maxHeight: "28px !important",
              lineHeight: "28px !important",
              marginBottom: "12px",
            },
            "& .MuiDataGrid-columnHeaderTitleContainer": {
              padding: "0 4px",
              alignItems: "center",
            },
            "& .MuiDataGrid-cell": {
              fontSize: "0.8rem",
              padding: "0 4px",
              display: "flex",
              alignItems: "center",
            },
            "& .MuiDataGrid-row:nth-of-type(odd)": {
              backgroundColor: "#f9f9f9", // light grey
            },
            "& .MuiDataGrid-row:nth-of-type(even)": {
              backgroundColor: "#f0f0f0", // slightly darker grey
            },

            // optional: keep hover highlight
            "& .MuiDataGrid-row:hover": {
              backgroundColor: "#e6e6e6",
            },
            "& .MuiDataGrid-virtualScroller": {
              backgroundColor: "#ffffff",
            },
          },
        },
        {
          props: { variant: "dense" },
          style: {
            "& .MuiDataGrid-columnHeader": {
              padding: "0 4px",
            },
            "& .MuiDataGrid-columnHeaders": {
              fontSize: "0.75rem",
              minHeight: "28px !important",
              maxHeight: "28px !important",
              lineHeight: "28px !important",
              marginBottom: "20px",
            },
            "& .MuiDataGrid-columnHeaderTitleContainer": {
              padding: "0px",
              alignItems: "center", // vertical center
            },
            "& .MuiDataGrid-cell": {
              fontSize: "0.75rem",
              padding: "0 4px",
              display: "flex",
              alignItems: "center", // vertical center text in cells
              height: "26px", // prevents overflow clipping
            },
            "& .MuiDataGrid-row": {
              minHeight: "26px !important",
              maxHeight: "26px !important",
            },

            // Green row backgrounds
            "& .MuiDataGrid-row.even.bg-green": { backgroundColor: "#d9f0df" },
            "& .MuiDataGrid-row.odd.bg-green": { backgroundColor: "#e6f4ea" },
            "& .MuiDataGrid-row.bg-green:hover": { backgroundColor: "#c8e6c9" },

            // Orange row backgrounds
            "& .MuiDataGrid-row.even.bg-orange": { backgroundColor: "#ffeacc" },
            "& .MuiDataGrid-row.odd.bg-orange": { backgroundColor: "#fff4e5" },
            "& .MuiDataGrid-row.bg-orange:hover": {
              backgroundColor: "#ffe0b2",
            },

            // Pink row backgrounds
            "& .MuiDataGrid-row.even.bg-pink": { backgroundColor: "#f8bbd0" },
            "& .MuiDataGrid-row.odd.bg-pink": { backgroundColor: "#fce4ec" },
            "& .MuiDataGrid-row.bg-pink:hover": { backgroundColor: "#f48fb1" },

            // Blue row backgrounds
            "& .MuiDataGrid-row.even.bg-blue": { backgroundColor: "#d0e8ff" },
            "& .MuiDataGrid-row.odd.bg-blue": { backgroundColor: "#e3f2fd" },
            "& .MuiDataGrid-row.bg-blue:hover": { backgroundColor: "#bbdefb" },

            // Yellow row backgrounds
            "& .MuiDataGrid-row.even.bg-yellow": { backgroundColor: "#fff9c4" },
            "& .MuiDataGrid-row.odd.bg-yellow": { backgroundColor: "#fffde7" },
            "& .MuiDataGrid-row.bg-yellow:hover": {
              backgroundColor: "#fff59d",
            },

            // Light row backgrounds
            "& .MuiDataGrid-row.even.bg-light": { backgroundColor: "#f5f5f5" },
            "& .MuiDataGrid-row.odd.bg-light": { backgroundColor: "#fafafa" },
            "& .MuiDataGrid-row.bg-light:hover": { backgroundColor: "#eeeeee" },

            // Grey row backgrounds
            "& .MuiDataGrid-row.even.bg-grey": { backgroundColor: "#e0e0e0" },
            "& .MuiDataGrid-row.odd.bg-grey": { backgroundColor: "#eeeeee" },
            "& .MuiDataGrid-row.bg-grey:hover": { backgroundColor: "#bdbdbd" },

            // Red row backgrounds
            "& .MuiDataGrid-row.even.bg-red": { backgroundColor: "#ffcdd2" },
            "& .MuiDataGrid-row.odd.bg-red": { backgroundColor: "#ffebee" },
            "& .MuiDataGrid-row.bg-red:hover": { backgroundColor: "#ef9a9a" },
          },
        },
      ],
    },
  },
});
export default mybotgpTheme;
